import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useFormGuard } from "@/hooks/useFormGuard";
import { UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCreateOwner, useLinkPropertyToOwner } from "@/hooks/useOwners";
import { useAuth } from "@/hooks/useAuth";
import { useUnifiedInvite } from "@/hooks/useUnifiedInvite";
import { getSupabaseErrorMessage } from "@/lib/supabase-errors";
import { isExpectedNestClientError, isNestAuthError } from "@/lib/networkErrors";
import { propertiesRepository } from "@/repositories/properties";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { PhoneInput } from "@/components/auth/PhoneInput";
import { isValidSaudiPhone, normalizePhoneForDB } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { InvitationSuccessModal } from "@/components/invitations/InvitationSuccessModal";

const formSchema = z
  .object({
    owner_type: z.enum(["individual", "commercial"]).default("individual"),
    full_name: z.string().min(2, "الاسم مطلوب"),
    full_name_en: z.string().optional(),
    national_id: z.string().min(10, "رقم الهوية مطلوب"),
    // Form holds 9-digit local form (`504148369`); normalized to canonical at submit.
    phone: z.string().refine(isValidSaudiPhone, "رقم الجوال غير صالح"),
    email: z.string().email("بريد إلكتروني غير صالح").optional().or(z.literal("")),
    address: z.string().optional(),
    property_id: z.string().optional(),
    // Commercial-entity fields (mirror the tenant form)
    commercial_name: z.string().optional(),
    commercial_name_en: z.string().optional(),
    unified_number: z.string().optional(),
    cr_number: z.string().optional(),
    // VAT number (الرقم الضريبي) — optional, available to BOTH owner types: an
    // individual may own a commercial unit and need a VAT number to collect
    // commercial rent (residential rent is VAT-exempt). If provided, a Saudi VAT
    // TIN: 15 digits, first and last digit 3 (matches the migration comment; the
    // DB stays unconstrained for historical/import rows).
    vat_number: z
      .string()
      .optional()
      .refine((v) => !v || /^3\d{13}3$/.test(v), "رقم ضريبي سعودي غير صالح (١٥ رقماً يبدأ وينتهي بـ ٣) | Invalid Saudi VAT number (15 digits, starts & ends with 3)"),
    // Representative (الممثل) — optional for both types
    representative_name: z.string().optional(),
    representative_name_en: z.string().optional(),
    representative_id_number: z.string().optional(),
    representative_phone: z
      .string()
      .optional()
      .or(z.literal(""))
      .refine((v) => !v || isValidSaudiPhone(v), "رقم الجوال غير صالح"),
  date_of_birth: z.string().optional(),
  })
  // Commercial owners must carry a commercial name + CR number, matching the
  // tenant form's validation.
  .superRefine((val, ctx) => {
    if (val.owner_type === "commercial") {
      if (!val.commercial_name || val.commercial_name.length < 2) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["commercial_name"], message: "اسم المنشأة مطلوب" });
      }
      if (!val.cr_number) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cr_number"], message: "رقم السجل التجاري مطلوب" });
      }
    }
  });

type FormData = z.infer<typeof formSchema>;

interface AddOwnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (ownerId: string) => void;
}

export function AddOwnerDialog({ open, onOpenChange, onSuccess }: AddOwnerDialogProps) {
  const { t, language } = useLanguage();
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const createOwner = useCreateOwner();
  const linkProperty = useLinkPropertyToOwner();
  const unifiedInvite = useUnifiedInvite();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<{
    inviteLink: string;
    whatsappMessage: string;
    recipientName: string;
    recipientPhone: string;
    recipientEmail?: string;
  } | null>(null);

  // Get properties without owners
  const { data: availableProperties = [] } = useQuery({
    queryKey: ["properties-without-owners", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      return propertiesRepository.listWithoutOwner(companyId);
    },
    enabled: !!companyId && open,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      owner_type: "individual",
      full_name: "",
      full_name_en: "",
      national_id: "",
      phone: "",
      email: "",
      address: "",
      property_id: "",
      commercial_name: "",
      commercial_name_en: "",
      unified_number: "",
      cr_number: "",
      vat_number: "",
      representative_name: "",
      representative_name_en: "",
      representative_id_number: "",
      representative_phone: "",
      date_of_birth: "",
    },
  });
  const ownerType = form.watch("owner_type");
  const blocker = useFormGuard(open && form.formState.isDirty);

  // Date-of-birth bounds: a DOB can't be in the future, and we offer a 120-year
  // lookback so the year dropdown isn't floored at currentYear-50 (~1976).
  const dobMaxDate = useMemo(() => new Date(), []);
  const dobMinDate = useMemo(() => new Date(dobMaxDate.getFullYear() - 120, 0, 1), [dobMaxDate]);

  // Reset the form + clear any submit error whenever the dialog closes, so
  // reopening starts clean instead of showing stale values and a stuck error.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      form.reset();
      setFormError(null);
      setIsSubmitting(false);
    }
    onOpenChange(next);
  };

  const onSubmit = async (values: FormData) => {
    setIsSubmitting(true);
    setFormError(null);
    const canonicalPhone = normalizePhoneForDB(values.phone)!;
    try {
      const isCommercial = values.owner_type === "commercial";
      const owner = await createOwner.mutateAsync({
        owner_type: values.owner_type,
        full_name: values.full_name,
        full_name_en: values.full_name_en || null,
        national_id: values.national_id,
        phone: canonicalPhone,
        email: values.email || null,
        address: values.address || null,
        commercial_name: isCommercial ? values.commercial_name || null : null,
        commercial_name_en: isCommercial ? values.commercial_name_en || null : null,
        unified_number: isCommercial ? values.unified_number || null : null,
        cr_number: isCommercial ? values.cr_number || null : null,
        vat_number: values.vat_number || null,
        representative_name: values.representative_name || null,
        representative_name_en: values.representative_name_en || null,
        representative_id_number: values.representative_id_number || null,
        representative_phone: normalizePhoneForDB(values.representative_phone) || null,
        date_of_birth: values.date_of_birth || null,
      });

      // If a property was selected, link it to the owner
      if (values.property_id && values.property_id !== "none" && owner) {
        await linkProperty.mutateAsync({
          propertyId: values.property_id,
          ownerId: owner.id,
        });
      }

      toast.success(t("تم إضافة المالك بنجاح", "Owner added successfully"));
      queryClient.invalidateQueries({ queryKey: ["properties-without-owners"] });

      // Notify parent with new owner ID before closing
      onSuccess?.(owner.id);
      onOpenChange(false);
      form.reset();

      // Generate an invitation link/token (no SMS or email is sent here —
      // delivery is opt-in from the InvitationSuccessModal that opens next).
      try {
        const result = await unifiedInvite.mutateAsync({
          profileType: "owner",
          entityId: owner.id,
          fullName: values.full_name,
          phone: canonicalPhone,
        });
        setInviteResult({
          inviteLink: result.inviteLink,
          whatsappMessage: result.whatsappMessage,
          recipientName: values.full_name,
          recipientPhone: canonicalPhone,
          recipientEmail: values.email || undefined,
        });
      } catch {
        // Link generation failure is non-blocking — owner is already saved.
      }
    } catch (error: unknown) {
      // 4xx domain rejections (e.g. duplicate identity, 409) are expected and
      // already surfaced via toast/formError below — console.error would
      // re-report them to Sentry through captureConsoleIntegration, PII
      // included (#3075). kind=auth (signout race mid-submit) is equally
      // expected and self-healing. Only log unexpected failures.
      if (!isExpectedNestClientError(error) && !isNestAuthError(error)) {
        if (error instanceof Error) {
          console.error("Error creating owner:", error.message);
        } else {
          console.error("Error creating owner:", error);
        }
      }

      const { ar, en } = getSupabaseErrorMessage(error, {
        ar: "حدث خطأ أثناء إضافة المالك",
        en: "Error adding owner",
      });
      const msg = t(ar, en);
      setFormError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("إضافة مالك", "Add Owner")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("أدخل بيانات المالك الجديد لإضافته إلى النظام وربطه بعقار اختيارياً.", "Enter the new owner's details to add them to the system and optionally link them to a property.")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Owner type — individual vs organization (mirrors tenant form) */}
            <Tabs
              value={ownerType}
              onValueChange={(v) => form.setValue("owner_type", v as "individual" | "commercial")}
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="individual">{t("فرد", "Individual")}</TabsTrigger>
                <TabsTrigger value="commercial">{t("منشأة تجارية", "Organization")}</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Commercial-entity section — only for organization owners */}
            {ownerType === "commercial" && (
              <div className="space-y-4 rounded-md border p-3">
                <p className="text-sm font-medium text-muted-foreground">
                  {t("بيانات المنشأة", "Organization details")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="commercial_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("اسم المنشأة", "Organization Name")} *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder={t("اسم المنشأة بالعربية", "Arabic name")} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="commercial_name_en"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("اسم المنشأة (إنجليزي)", "Organization (English)")}</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="English name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="cr_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("رقم السجل التجاري", "CR Number")} *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="1010123456" dir="ltr" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="unified_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("الرقم الموحد", "Unified Number")}</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="7001234567" dir="ltr" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {ownerType === "commercial"
                        ? `${t("اسم المسؤول", "Contact Person")} *`
                        : `${t("الاسم الكامل", "Full Name")} *`}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t("الاسم بالعربية", "Arabic name")} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="full_name_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("الاسم (إنجليزي)", "Name (English)")}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="English name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="national_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("رقم الهوية", "National ID")} *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="1000000000" dir="ltr" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="date_of_birth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("تاريخ الميلاد", "Date of Birth")}</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      locale={language === "ar" ? "ar" : "en"}
                      minDate={dobMinDate}
                      maxDate={dobMaxDate}
                      showHijri
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("رقم الجوال", "Phone")} *</FormLabel>
                    <FormControl>
                      <PhoneInput value={field.value || ""} onChange={(v) => field.onChange(v)} onBlur={field.onBlur} hideInvalidHint />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("البريد الإلكتروني", "Email")}</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="email@example.com" dir="ltr" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("العنوان", "Address")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t("العنوان", "Address")} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* VAT number — optional, shown for both individual and commercial
                owners (an individual can be VAT-registered under their own name). */}
            <FormField
              control={form.control}
              name="vat_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("الرقم الضريبي", "VAT Number")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="300000000000003" dir="ltr" inputMode="numeric" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Representative (الممثل) — optional authorised representative,
                shown for both individual and organization owners. */}
            <Separator />
            <div className="space-y-4">
              <p className="text-sm font-medium text-muted-foreground">
                {t("بيانات الممثل (اختياري)", "Representative (optional)")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="representative_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("اسم الممثل", "Representative Name")}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={t("الاسم بالعربية", "Arabic name")} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="representative_id_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("هوية الممثل", "Representative ID")}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="1000000000" dir="ltr" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="representative_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("جوال الممثل", "Representative Phone")}</FormLabel>
                    <FormControl>
                      <PhoneInput value={field.value || ""} onChange={(v) => field.onChange(v)} onBlur={field.onBlur} hideInvalidHint />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="property_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ربط عقار", "Link Property")}</FormLabel>
                  <Select
                    onValueChange={(val) => field.onChange(val === "none" ? "" : val)}
                    value={field.value || "none"}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("اختر عقاراً (اختياري)", "Select property (optional)")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">{t("بدون عقار", "No property")}</SelectItem>
                      {availableProperties.map((property) => (
                        <SelectItem key={property.id} value={property.id}>
                          {property.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {formError && (
              <p className="text-sm text-destructive">{formError}</p>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                {t("إلغاء", "Cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin me-2" />}
                {t("إضافة مالك", "Add Owner")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    <UnsavedChangesDialog blocker={blocker} />
    {inviteResult && (
      <InvitationSuccessModal
        open={!!inviteResult}
        onOpenChange={(o) => { if (!o) setInviteResult(null); }}
        inviteLink={inviteResult.inviteLink}
        whatsappMessage={inviteResult.whatsappMessage}
        recipientPhone={inviteResult.recipientPhone}
        recipientName={inviteResult.recipientName}
        recipientEmail={inviteResult.recipientEmail}
        profileType="owner"
      />
    )}
    </>
  );
}
